import QuickItemViewBox from "../../components/DataDisplayer/QuickItemViewBox";
import { ISelectFieldSingleItem } from "../../components/Form/DynamicForm";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppFormConfig, ISearchParam } from "../BaseApp";
import ItemsApp, { IItemModel } from "../items/ItemsApp";
import { useReturnAllItems } from "../items/itemsSlice";
import LocationsApp from "../locations/LocationsApp";
import { useReturnAllLocations } from "../locations/locationsSlice";
import ProjectsApp from "../projects/ProjectsApp";
import { useReturnAllProjects } from "../projects/projectsSlice";
import SectionsApp from "../sections/SectionsApp";
import { useReturnAllSections } from "../sections/sectionsSlice";
import WarehousesApp from "../warehouses/WarehousesApp";
import { useReturnAllWarehouses } from "../warehouses/warehousesSlice";
import GenerateTransactionDestination from "./GenerateTransactionDestination";

/**
 * Item Model
 */
export interface ITransactionModal{
    id: number,
    item_id: number,
    user_id: number,
    action: string,
    type: string,
    timestamp: string,
    warehouse_id: number,
    section_id: number,
    location_id: number,
    warehouse_title: string,
    section_title: string,
    category_title: string,
    item_title: string,
    created_by_name: string,
    original_warehouse_id: number,
    original_section_id: number,
    original_location_id: number,
    original_warehouse_title: string,
    original_section_title: string,
    original_location_title: string,
    original_user_title: string,
    original_user_id: number,
    location_title: string,
    user_full_name: string,
    project_title: string,
    project_id: number,
    quantity: number,
    note: string,
    request_ref?: string,
}

export const EmptyTransactionModel: ITransactionModal = {
    id: 0,
    item_id: 0,
    user_id: 0,
    action: "",
    type: "",
    timestamp: "",
    warehouse_id: 0,
    section_id: 0,
    location_id: 0,
    warehouse_title: "",
    section_title: "",
    category_title: "",
    item_title: "",
    created_by_name: "",
    original_warehouse_id: 0,
    original_section_id: 0,
    original_location_id: 0,
    original_warehouse_title: "",
    original_section_title: "",
    original_location_title: "",
    location_title: "",
    user_full_name: "",
    original_user_title: "",
    original_user_id: 0,
    project_title: "",
    quantity: 0,
    project_id: 0,
    note: ""
}

/**
 * Transcations Order
 */
export type TransactionOrderStatus = "progress" | "success" | "failed" | "not-started"
export interface ITransactionOrder {
    id: number,
    status: TransactionOrderStatus,
    item_id: number,
    user_id: number,
    action: string,
    type: string,
    timestamp: string,
    warehouse_id: number,
    section_id: number,
    location_id: number,
    warehouse_title: string,
    section_title: string,
    location_title: string,
    user_title: string,
    item: IItemModel,
    message: string,
    project_id: number,
    isLoading: boolean,
}

export const transactionsTypesList: Array<ISelectFieldSingleItem> = [
    {
        name: "Move to a warehouse",
        value: "to-warehouse"
    },
    {
        name: "Move to a section",
        value: "to-section"
    },
    {
        name: "Move to a location",
        value: "to-location"
    },
    {
        name: "With a user",
        value: "with-user"
    },
    {
        name: "Stock Adjustment",
        value: "stock-adjustment"
    }
]

export const transactionsActionsList: Array<ISelectFieldSingleItem> = [
    {
        name: "Taken",
        value: "taken"
    },
    {
        name: "Returned",
        value: "returned"
    },
    {
        name: "Transferred",
        value: "transferred"
    },
    {
        name: "New Stock",
        value: "new-item"
    },
    {
        name: "Reduce Stock",
        value: "reduce-stock"
    }
]

interface ITransactionsApp extends IBaseApp {
    listOfInstancesForItem: (itemId: number, page: number, search: string|null,  disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => Promise<any>,
    returnItemToOrigin: (itemId: number) => Promise<any>,
}

/**
 * Transactions App
 */
export default class TransactionsApp extends BaseApp implements ITransactionsApp {

    appName = "transactions"
    appBaseEndPoint = "/items/transactions"

    customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "transactions_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "action",
                type: "status",
                label: "Action",
                maxWidth: 150,
                minWidth: 150,
            },
            {
                id: "type",
                type: "status",
                label: "Transferred to",
                maxWidth: 200,
                minWidth: 200,
                customContent :(item) =>{

                    let title: string = ""

                    if (item.action == "returned"){
                        switch(item.type){

                            case "to-warehouse":
                                title = "Original Warehouse"
                                break

                            case "to-section":
                                title = "Original Section"
                                break

                            case "to-location":
                                title = "Original Location"
                                break

                        }
                    }

                    return <StatusChip type={item.type} customLabel={title} />
                }
            },
            {
                id: "item_title",
                type: "text",
                label: "Item",
                maxWidth: 200,
                minWidth: 200,
                quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllItems} itemId={item.item_id} baseApp={new ItemsApp()} />
            },
            {
                id: "project_title",
                type: "text",
                label: "Project",
                maxWidth: 200,
                minWidth: 200,
                quickItemView : (item, shown, handleHide) => {
                    return <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllProjects} itemId={item.project_id} baseApp={new ProjectsApp()} />
                }
            },
            {
                id: "warehouse_title",
                type: "text",
                label: "Destination",
                maxWidth: 350,
                minWidth: 350,
                customContent: (item) => {
                    return <GenerateTransactionDestination transcation={(item as ITransactionModal)} />
                }
            },
            {
                id: "warehouse_title",
                type: "text",
                label: "Origin",
                maxWidth: 350,
                minWidth: 350,
                customContent: (item) => {
                    return <GenerateTransactionDestination transcation={(item as ITransactionModal)} showOrigin />
                }
            },
            {
                id: "quantity",
                type: "text",
                label: "Quantity",
                maxWidth: 100,
                minWidth: 100,
            },
            // {
            //     id: "warehouse_title",
            //     type: "text",
            //     label: "Warehouse",
            //     maxWidth: 200,
            //     minWidth: 200,
            //     quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllWarehouses} itemId={item.warehouse_id} baseApp={new WarehousesApp()} />
            // },
            // {
            //     id: "section_title",
            //     type: "text",
            //     label: "Section",
            //     maxWidth: 200,
            //     minWidth: 200,
            //     quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllSections} itemId={item.section_id} baseApp={new SectionsApp()} />
            // },
            // {
            //     id: "location_title",
            //     type: "text",
            //     label: "Location",
            //     maxWidth: 200,
            //     minWidth: 200,
            //     quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllLocations} itemId={item.location_id} baseApp={new LocationsApp()} />
            // },
            {
                id: "created_by_name",
                type: "text",
                label: "User",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "timestamp",
                type: "date",
                label: "Timestamp",
                maxWidth: 250,
                minWidth: 250,
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No transactions have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "transactions_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 12,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the item details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
        ]
    }

    /**
     * List of instances
     */
    listOfInstancesForItem = async(itemId: number, page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}&item_id=${itemId}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * List of instances
     */
    itemTransactionsList = async(itemId: number, page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => {

        let path: string = this.appBaseEndPoint + "/item/" + itemId

         path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }
        

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * Return item to orgin
     */
    returnItemToOrigin = async(itemId: number) => {
        return await api.post(this.appBaseEndPoint + "/return-item/" + itemId)
    }

    /**
     * Return type based on action
     */
    returnTypeBasedOnAction = (action: string, addPreposition?: boolean, type?: string, defaultVal?: string) => {

        let title: string = ""
        let actionTitle: string = ""
        let preposition: string = ""

        switch(action){

            case "taken":
                actionTitle = "Use"
                preposition = "in"

                if (type == "user" || type == "with-user"){
                    actionTitle = "Give"
                    preposition = "to"
                }

                break

            case "use":
                actionTitle = "Use"
                preposition = "in"

                if (type == "user" || type == "with-user"){
                    actionTitle = "Give"
                    preposition = "to"
                }
                break

            case "returned":
                actionTitle = "Return"
                preposition = "to"
                break

            case "transferred":
                actionTitle = "Transfer"
                preposition = "to"
                break

            case "move":
                actionTitle = "Move"
                preposition = "to"
                break
        }

        if (addPreposition){
            title = actionTitle + " " + preposition
        }

        if (type){
            let destination: string = ""

            switch(type){

                case "to-warehouse":
                    destination = "warehouse"
                    break
                
                case "to-section":
                    destination = "section"
                    break

                case "to-location":
                    destination = "location"
                    break
            }

            title = actionTitle + " " + preposition + " " + destination

            if (type == "with-user" || type == "user"){
                title = "Give to user"
            }

        }

        if (defaultVal){
            if (!title){
                return defaultVal
            }
        }

        return title
    }

    returnFromTypeBasedOnAction = (action: string, addPreposition?: boolean, type?: string, defaultVal?: string) => {

        let title: string = ""
        let actionTitle: string = ""
        let preposition: string = ""

        switch(action){

            case "taken":
                actionTitle = "Used"
                preposition = "in"
                break

            case "returned":
                actionTitle = "Return"
                preposition = "from"
                break

            case "transferred":
                actionTitle = "Transfer"
                preposition = "from"
                break
        }

        if (addPreposition){
            title = actionTitle + " " + preposition
        }

        if (type){
            let destination: string = ""

            switch(type){

                case "to-warehouse":
                    destination = "warehouse"
                    break
                
                case "to-section":
                    destination = "section"
                    break

                case "to-location":
                    destination = "location"
                    break
            }

            title = actionTitle + " " + preposition + " " + destination

            if (type == "with-user"){
                title = "From user"
            }

        }

        if (defaultVal){
            if (!title){
                return defaultVal
            }
        }

        return title
    }

}